import { must } from '../../shared/assert';
import { validateServerMessage } from '../../shared/protocol';
import { ErrorCode, type GameState } from '../../shared/types/domain';
import type { S2C } from '../../shared/types/protocol';
import { getConnectCloseToastMessage } from '../messages/server-error-presentation';
import { TOAST } from '../messages/toasts';
import { deriveReconnectAttemptPlan } from './network';
import type { ClientState } from './phase';
import { buildWebSocketUrl } from './session-links';
import type { ReconnectOverlayState } from './session-ui-state';
import { createWebSocketTransport, type GameTransport } from './transport';
export interface ConnectionDeps {
  getGameCode: () => string | null;
  getGameState: () => GameState | null;
  getClientState: () => ClientState;
  isSpectatorSession: () => boolean;
  getStoredPlayerToken: (code: string) => string | null;
  getReconnectAttempts: () => number;
  setReconnectAttempts: (n: number) => void;
  setTransport: (t: GameTransport) => void;
  setLatencyMs: (ms: number) => void;
  setReconnectOverlayState: (state: ReconnectOverlayState | null) => void;
  setState: (state: ClientState) => void;
  handleMessage: (msg: S2C) => void;
  showToast: (msg: string, type: 'error' | 'info' | 'success') => void;
  exitToMenu: () => void;
  trackEvent: (event: string, props?: Record<string, unknown>) => void;
  webSocketCtor: typeof WebSocket;
}

export interface ConnectionManager {
  connect: (code: string) => void;
  send: (msg: unknown) => void;
  startPing: () => void;
  stopPing: () => void;
  attemptReconnect: () => void;
  handleDisconnect: () => void;
  close: () => void;
  getWs: () => WebSocket | null;
  recordLatencySample: (latencyMs: number) => void;
}

interface LatencyAccumulator {
  sessionStartedAt: number;
  samples: number;
  sumMs: number;
  minMs: number;
  maxMs: number;
}

interface ConnectionRuntime {
  ws: WebSocket | null;
  pingInterval: number | null;
  reconnectTimer: number | null;
  suppressDisconnectHandling: boolean;
  /** Last WebSocket close from the active transport (for UX + telemetry). */
  lastClose: { code: number; reason: string; wasClean: boolean } | null;
  /** Per-WS latency stats; reset on every fresh `connect()`. */
  latency: LatencyAccumulator | null;
}

const createLatencyAccumulator = (): LatencyAccumulator => ({
  sessionStartedAt: Date.now(),
  samples: 0,
  sumMs: 0,
  minMs: Number.POSITIVE_INFINITY,
  maxMs: 0,
});

const PING_INTERVAL_MS = 5000;
const MAX_RECONNECT_ATTEMPTS = 5;
const REPLACED_CLOSE_REASON = 'Replaced by new connection';

const clearPingInterval = (
  runtime: Pick<ConnectionRuntime, 'pingInterval'>,
): void => {
  if (runtime.pingInterval !== null) {
    clearInterval(runtime.pingInterval);
    runtime.pingInterval = null;
  }
};

const clearReconnectTimer = (
  runtime: Pick<ConnectionRuntime, 'reconnectTimer'>,
): void => {
  if (runtime.reconnectTimer !== null) {
    clearTimeout(runtime.reconnectTimer);
    runtime.reconnectTimer = null;
  }
};

const parseServerPayload = (
  payload: string,
  deps: Pick<ConnectionDeps, 'trackEvent'>,
): S2C | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(payload);
  } catch {
    deps.trackEvent('ws_parse_error');
    return null;
  }
  const result = validateServerMessage(parsed);
  if (!result.ok) {
    deps.trackEvent('ws_invalid_message', { error: result.error });
    return null;
  }
  return result.value;
};

export const createConnectionManager = (
  deps: ConnectionDeps,
): ConnectionManager => {
  const WebSocketCtor = deps.webSocketCtor;
  const runtime: ConnectionRuntime = {
    ws: null,
    pingInterval: null,
    reconnectTimer: null,
    suppressDisconnectHandling: false,
    lastClose: null,
    latency: null,
  };

  const flushSessionQuality = (closeCode: number | null): void => {
    const acc = runtime.latency;
    runtime.latency = null;
    if (!acc || acc.samples === 0) return;
    deps.trackEvent('ws_session_quality', {
      durationMs: Date.now() - acc.sessionStartedAt,
      samples: acc.samples,
      latencyAvgMs: Math.round(acc.sumMs / acc.samples),
      latencyMinMs: acc.minMs,
      latencyMaxMs: acc.maxMs,
      closeCode,
    });
  };

  const recordLatencySample = (latencyMs: number): void => {
    if (latencyMs < 0) return;
    const acc = runtime.latency ?? createLatencyAccumulator();
    runtime.latency = acc;
    acc.samples += 1;
    acc.sumMs += latencyMs;
    if (latencyMs < acc.minMs) acc.minMs = latencyMs;
    if (latencyMs > acc.maxMs) acc.maxMs = latencyMs;
  };

  const send = (msg: unknown) => {
    if (runtime.ws?.readyState === WebSocketCtor.OPEN) {
      runtime.ws.send(JSON.stringify(msg));
    }
  };

  const stopPing = () => {
    clearPingInterval(runtime);
    deps.setLatencyMs(-1);
  };

  const startPing = () => {
    stopPing();
    runtime.pingInterval = window.setInterval(() => {
      if (runtime.ws?.readyState === WebSocketCtor.OPEN) {
        const sentAt = Date.now();
        send({ type: 'ping', t: sentAt });
      }
    }, PING_INTERVAL_MS);
  };

  const clearReconnectUi = () => {
    deps.setReconnectOverlayState(null);
  };

  const clearReconnectFlow = () => {
    clearReconnectTimer(runtime);
    clearReconnectUi();
  };

  const exitReconnectFlow = () => {
    clearReconnectFlow();
    deps.exitToMenu();
  };

  const handleSessionReplaced = (socket: WebSocket): void => {
    if (runtime.ws !== socket) {
      return;
    }
    runtime.ws = null;
    runtime.suppressDisconnectHandling = true;
    flushSessionQuality(1000);
    stopPing();
    clearReconnectFlow();
    deps.trackEvent('ws_session_replaced');
    deps.exitToMenu();
    socket.close(1000, REPLACED_CLOSE_REASON);
  };

  const handleSocketMessage = (socket: WebSocket, payload: string) => {
    const message = parseServerPayload(payload, deps);
    if (message) {
      deps.handleMessage(message);
      if (
        message.type === 'error' &&
        message.code === ErrorCode.SESSION_REPLACED
      ) {
        handleSessionReplaced(socket);
      }
    }
  };

  const attemptReconnect = () => {
    const reconnectAttempts = deps.getReconnectAttempts();
    const plan = deriveReconnectAttemptPlan(
      deps.getGameCode(),
      reconnectAttempts,
      MAX_RECONNECT_ATTEMPTS,
    );

    if (plan.giveUp) {
      deps.trackEvent('reconnect_failed', {
        attempts: reconnectAttempts,
      });
      clearReconnectFlow();
      deps.showToast(TOAST.connection.couldNotReconnect, 'error');
      deps.exitToMenu();
      return;
    }

    const attempt = must(plan.nextAttempt);
    const delayMs = must(plan.delayMs);

    deps.setReconnectAttempts(attempt);
    deps.trackEvent('reconnect_attempt_scheduled', {
      attempt,
      delayMs,
    });
    deps.setReconnectOverlayState({
      attempt,
      maxAttempts: MAX_RECONNECT_ATTEMPTS,
      onCancel: () => {
        exitReconnectFlow();
      },
    });
    runtime.reconnectTimer = window.setTimeout(() => {
      runtime.reconnectTimer = null;
      connect(must(deps.getGameCode()));
    }, delayMs);
  };

  const connectFailureToast = (): void => {
    const close = runtime.lastClose;
    deps.trackEvent('ws_connect_closed', {
      code: close?.code ?? null,
      wasClean: close?.wasClean ?? null,
      reasonLen: close?.reason?.length ?? 0,
    });
    if (!close || close.code === 1000) {
      deps.showToast(TOAST.connection.couldNotConnect, 'error');
      return;
    }
    deps.showToast(
      getConnectCloseToastMessage(close.code, close.reason),
      'error',
    );
  };

  const handleDisconnect = () => {
    stopPing();
    const currentState = deps.getClientState();
    const canReconnect =
      currentState !== 'menu' &&
      currentState !== 'gameOver' &&
      currentState !== 'connecting' &&
      Boolean(deps.getGameCode());

    if (canReconnect) {
      attemptReconnect();
      return;
    }

    if (currentState === 'menu' || currentState === 'gameOver') {
      return;
    }

    // Otherwise we were mid-flow without a reconnect target (menu-bound);
    // surface the connecting-failure toast and route back to the menu.
    if (currentState === 'connecting') {
      connectFailureToast();
    }
    deps.exitToMenu();
  };

  const handleSocketClose = (socket: WebSocket, ev: CloseEvent) => {
    if (runtime.ws !== socket) {
      return;
    }
    runtime.ws = null;
    runtime.lastClose = {
      code: ev.code,
      reason: ev.reason,
      wasClean: ev.wasClean,
    };
    flushSessionQuality(ev.code);

    if (ev.reason === REPLACED_CLOSE_REASON) {
      runtime.suppressDisconnectHandling = false;
      stopPing();
      clearReconnectFlow();
      deps.trackEvent('ws_session_replaced');
      deps.exitToMenu();
      return;
    }

    const shouldHandleDisconnect = !runtime.suppressDisconnectHandling;
    runtime.suppressDisconnectHandling = false;

    if (shouldHandleDisconnect) {
      handleDisconnect();
    }
  };

  const connect = (code: string) => {
    runtime.suppressDisconnectHandling = false;
    runtime.lastClose = null;
    const spectator = deps.isSpectatorSession();
    const previousSocket = runtime.ws;
    const socket = new WebSocketCtor(
      buildWebSocketUrl(
        location,
        code,
        spectator ? null : deps.getStoredPlayerToken(code),
        spectator ? { viewer: 'spectator' } : undefined,
      ),
    );
    runtime.ws = socket;
    previousSocket?.close();
    socket.onmessage = (e) => {
      handleSocketMessage(socket, e.data);
    };
    socket.onclose = (ev) => {
      handleSocketClose(socket, ev);
    };
    socket.onerror = () => {
      deps.trackEvent('ws_connect_error');
    };
    deps.setTransport(createWebSocketTransport((msg) => send(msg)));
    startPing();
  };

  const close = () => {
    runtime.suppressDisconnectHandling = true;
    stopPing();
    clearReconnectFlow();
    flushSessionQuality(null);
    runtime.ws?.close();
    runtime.ws = null;
  };

  return {
    connect,
    send,
    startPing,
    stopPing,
    attemptReconnect,
    handleDisconnect,
    close,
    getWs: () => runtime.ws,
    recordLatencySample,
  };
};
